import {
  AppName,
  Field,
  FieldMap,
  FieldMapValue,
  FieldTypes,
  FileDBValue,
  FileField,
  getFormElementHasAnyDataUrls,
  getFormElementHasAnyFileFields,
  getFormElementName,
  getUserGroup,
  isField,
  isFieldMap,
  isInfoStage,
  isListField,
  ListField,
  UnuploadedFileDBValue,
  UploadProgress,
} from '@hb/shared'
import { FirebaseStorage } from 'firebase/storage'
import cloneDeep from 'lodash.clonedeep'
import { deleteFile, getResizedStoragePath, uploadFile } from '../../storage/utils'

export const processFileFieldData = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  baseStoragePath: string,
  fieldPathSegments: string[],
  field: FileField,
  data: FileDBValue | UnuploadedFileDBValue,
  prevData: UnuploadedFileDBValue | FileDBValue | undefined | null,
  onUploadProgress: (progress: UploadProgress) => void,
  preventFileDeletion?: boolean,
  customStorage?: FirebaseStorage,
): Promise<FileDBValue | undefined | null> => {
  const deletePrevFile = async () => {
    if (prevData?.storagePath && !preventFileDeletion) {
      // delete file from storage
      await deleteFile(getResizedStoragePath(prevData.storagePath, prevData.type), customStorage)
    }
  }
  if (!data) {
    await deletePrevFile()
    return undefined
  }

  if (data.dataUrl) {
    await deletePrevFile()
    const storagePath = [baseStoragePath, ...fieldPathSegments].map(s => s.trim()).join('/')
    // get file from dataUrl
    const file = await fetch(data.dataUrl).then(res => res.blob())

    onUploadProgress({ complete: false, label: field.placeholder, progress: 0 })
    // upload file to storage
    await uploadFile(
      storagePath,
      file,
      progress =>
        onUploadProgress({
          complete: progress === 100,
          label: field.placeholder,
          progress,
        }),
      customStorage,
    )
    const now = Date.now()
    const updated: FileDBValue = {
      ...data,
      id: `${now}`,
      storagePath,
      type: file.type,
      uploadedOn: now,
      uploadedByGroup: getUserGroup(appName, admin),
    }
    if (uid) updated.uploadedBy = uid
    delete updated.dataUrl
    return updated
  }
  return data as FileDBValue
}

const processFieldData = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  baseStoragePath: string,
  field: Field,
  data: any,
  prevData: any,
  onUploadProgress: (progress: UploadProgress) => void,
  fieldPathSegments: string[],
  preventFileDeletion?: boolean,
  customStorage?: FirebaseStorage,
) => {
  switch (field.type) {
    case FieldTypes.FILE:
      return processFileFieldData(
        appName,
        admin,
        uid,
        baseStoragePath,
        fieldPathSegments,
        field,
        data,
        prevData,
        onUploadProgress,
        preventFileDeletion,
        customStorage,
      )
    default:
      return data
  }
}

export const processListFieldData = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  baseStoragePath: string,
  field: ListField,
  data: any[] | undefined,
  prevData: any[] | undefined,
  onUploadProgress: (progress: Record<string, UploadProgress>) => void,
  fieldPathSegments: string[],
  omitExtraFields?: boolean,
  preventFileDeletion?: boolean,
  customStorage?: FirebaseStorage,
) => {
  if (!data) return []
  let uploads: Record<string, UploadProgress> = {}
  const now = Date.now()
  const itemField = field.itemFields
  return Promise.all(
    data.map((item, i) => {
      if (isField(itemField)) {
        const prevValue = Array.isArray(prevData) ? prevData[i] : undefined
        const pathSegments = [...fieldPathSegments, `${now + i}`]
        return processFieldData(
          appName,
          admin,
          uid,
          baseStoragePath,
          itemField,
          item,
          prevValue,
          progress => {
            uploads[pathSegments.join('.')] = progress
            onUploadProgress(uploads)
          },
          pathSegments,
          preventFileDeletion,
          customStorage,
        )
      }

      const id = item?.id
      const prevValue = id ? prevData?.find(prev => prev.id === id) : undefined
      return processFieldMapData(
        appName,
        admin,
        uid,
        baseStoragePath,
        itemField,
        item,
        prevValue,
        progress => {
          uploads = { ...uploads, ...progress }
          onUploadProgress(uploads)
        },
        fieldPathSegments,
        omitExtraFields,
        preventFileDeletion,
        customStorage,
      ).then(updated => (updated ? { ...updated, id: id ?? `${now + i}` } : null))
    }),
  )
}

export const processFieldMapData = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  baseStoragePath: string,
  field: FieldMap,
  data: any,
  prevData: any,
  onUploadProgress: (progress: Record<string, UploadProgress>) => void,
  fieldPathSegments?: string[] | null,
  omitExtraFields?: boolean,
  preventFileDeletion?: boolean,
  customStorage?: FirebaseStorage,
): Promise<FieldMapValue> => {
  const cloned = cloneDeep(data ?? {})

  if (getFormElementHasAnyDataUrls(field, data) && !baseStoragePath) {
    throw new Error('Data URLs found in fields, no base storage path provided')
  }

  let uploads: Record<string, UploadProgress> = {}
  // find all nested file fields and upload them to storage
  const onProgress = (fieldKey: string, progress: UploadProgress) => {
    uploads[fieldKey] = progress
    onUploadProgress(uploads)
  }

  const onRecordProgress = (progress: Record<string, UploadProgress>) => {
    uploads = { ...uploads, ...progress }
    onUploadProgress(uploads)
  }

  const promises = Promise.all(
    Object.entries(field.children).map(async ([key, childField]) => {
      const fieldData = cloned[key]
      const combinedSegments = fieldPathSegments ? [...fieldPathSegments, key] : [key]
      if (isField(childField)) {
        const processed = await processFieldData(
          appName,
          admin,
          uid,
          baseStoragePath,
          childField,
          fieldData,
          prevData?.[key],
          progress => onProgress(combinedSegments.join('.'), progress),
          combinedSegments,
          preventFileDeletion,
          customStorage,
        )
        if (childField.type === FieldTypes.ID) {
          const itemId = key.endsWith('Id') ? key.slice(0, -2) : null
          if (itemId) delete cloned[itemId]
        }
        return {
          key,
          data: processed,
        }
      }
      if (isListField(childField)) {
        return {
          key,
          data: await processListFieldData(
            appName,
            admin,
            uid,
            baseStoragePath,
            childField,
            fieldData,
            prevData?.[key],
            progress => onRecordProgress(progress),
            combinedSegments,
            omitExtraFields,
            preventFileDeletion,
            customStorage,
          ),
        }
      }
      if (isInfoStage(childField)) {
        return null
        // do nothing
      }
      if (isFieldMap(childField)) {
        const processed = await processFieldMapData(
          appName,
          admin,
          uid,
          baseStoragePath,
          childField,
          fieldData,
          prevData?.[key],
          progress => {
            const withSegments = Object.entries(progress).reduce<Record<string, UploadProgress>>(
              (acc, [fieldKey, fieldProgress]) => ({
                ...acc,
                [`${combinedSegments.join('.')}.${fieldKey}`]: fieldProgress,
              }),
              {},
            )
            onRecordProgress(withSegments)
          },
          combinedSegments,
          omitExtraFields,
          preventFileDeletion,
          customStorage,
        )
        return {
          key,
          data:
            typeof processed === 'object' && Object.keys(processed).length > 0
              ? processed
              : undefined,
        }
      }
      return null
    }),
  )

  const res: Array<{ key: string; data: any } | null> = await promises

  onUploadProgress({})
  const unprocessedKeys = omitExtraFields
    ? []
    : Object.keys(cloned).filter(key => !field.children[key])
  const unprocessed = unprocessedKeys.reduce<FieldMapValue>(
    (acc, key) => ({ ...acc, [key]: cloned[key] }),
    {},
  )

  return res.reduce<FieldMapValue>((acc, withKey) => {
    if (!withKey) return acc
    const { data: fieldData, key } = withKey
    if (fieldData === undefined) return acc
    return { ...acc, [key]: fieldData }
  }, unprocessed)
}

export const processFormElementData = async (
  appName: AppName,
  admin: boolean,
  uid: string,
  baseStoragePath: string | undefined,
  field: Field | FieldMap | ListField,
  data: any,
  prevData: any,
  onUploadProgress: (progress: Record<string, UploadProgress>) => void,
  fieldPathSegments?: string[],
  omitExtraFields?: boolean,
  preventFileDeletion?: boolean,
  customStorage?: FirebaseStorage,
) => {
  if (getFormElementHasAnyFileFields(field) && !baseStoragePath) {
    const fieldName = getFormElementName(field)
    throw new Error(`baseStoragePath is required for file fields - missing in field ${fieldName}`)
  }
  if (isField(field)) {
    return processFieldData(
      appName,
      admin,
      uid,
      baseStoragePath ?? '',
      field,
      data,
      prevData,
      v => onUploadProgress({ value: v }),
      fieldPathSegments && Array.isArray(fieldPathSegments) ? fieldPathSegments : [],
      preventFileDeletion,
      customStorage,
    )
  }
  if (isListField(field)) {
    return processListFieldData(
      appName,
      admin,
      uid,
      baseStoragePath ?? '',
      field,
      data,
      prevData,
      onUploadProgress,
      fieldPathSegments && Array.isArray(fieldPathSegments) ? fieldPathSegments : [],
      omitExtraFields,
      preventFileDeletion,
      customStorage,
    )
  }
  return processFieldMapData(
    appName,
    admin,
    uid,
    baseStoragePath ?? '',
    field,
    data,
    prevData,
    onUploadProgress,
    fieldPathSegments && Array.isArray(fieldPathSegments) ? fieldPathSegments : [],
    omitExtraFields,
    preventFileDeletion,
    customStorage,
  )
}
